import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Navigation } from './components/navigation/Navigation';
import { Homepage } from './pages/Homepage';
import { AdminFarmersDirectory } from './pages/AdminFarmersDirectory';
import { UserFarmersDirectory } from './pages/UserFarmersDirectory';
import { AdminProcessorsDirectory } from './pages/AdminProcessorsDirectory';
import { UserProcessorsDirectory } from './pages/UserProcessorsDirectory';
import { Analytics } from './pages/Analytics';
import { About } from './pages/About';
import { Maps } from './pages/Maps';
import { AdminMap } from './pages/adminMap';
import { LoginForm } from './components/forms/LoginForm';
import { SignUpForm } from './components/forms/SignUpForm';
import { AdminApplicationForm } from './components/forms/AdminApplicationForm';
import { WelcomePage } from './pages/WelcomePage';
import { UserProfile } from './pages/UserProfile';
import { AdminProfile } from './pages/AdminProfile';
import Services from './pages/Services';
import AdminServices from './pages/AdminServices';
import { PendingApplications } from './pages/PendingApplications';
import { Settings } from './pages/Settings';
import { NotFound } from './pages/NotFound';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { authAPI, adminAPI } from './services/api';

export default function App() {
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showAdminApplication, setShowAdminApplication] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from sessionStorage on app initialization
  useEffect(() => {
    try {
      const savedUser = sessionStorage.getItem('bciis_user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      }
    } catch (error) {
      sessionStorage.removeItem('bciis_user');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    const savedUser = sessionStorage.getItem('bciis_user');
    let mergedUser = userData;
    if (savedUser) {
      try {
        const existingUser = JSON.parse(savedUser);
        if (!userData.avatar && existingUser.avatar && existingUser.username === userData.username) {
          mergedUser = { ...userData, avatar: existingUser.avatar };
        }
      } catch (error) { /* ignore */ }
    }
    setUser(mergedUser);
    sessionStorage.setItem('bciis_user', JSON.stringify(mergedUser));
    setShowLogin(false);
  };

  const handleSignUp = (userData) => {
    setUser(userData);
    sessionStorage.setItem('bciis_user', JSON.stringify(userData));
    setShowSignUp(false);
  };

  const handleLogout = () => {
    setUser(null);
    sessionStorage.removeItem('bciis_user');
    sessionStorage.removeItem('bciis_auth_token');
  };

  const switchToSignUp = () => {
    setShowLogin(false);
    setShowSignUp(true);
  };

  const switchToLogin = () => {
    setShowSignUp(false);
    setShowLogin(true);
  };

  const switchToAdminApplication = () => {
    setShowLogin(false);
    setShowSignUp(false);
    setShowAdminApplication(true);
  };

  const handleUpdateUser = async (updatedUserData) => {
    try {
      const mergedUserData = { ...user, ...updatedUserData };
      setUser(mergedUserData);
      sessionStorage.setItem('bciis_user', JSON.stringify(mergedUserData));

      if (user.role === 'admin') {
        await adminAPI.updateProfile(user.id, updatedUserData);
      } else {
        const response = await authAPI.updateProfile(updatedUserData);
        if (response.success && response.user) {
          setUser(response.user);
          sessionStorage.setItem('bciis_user', JSON.stringify(response.user));
        }
      }
    } catch (error) {
      // Local update already applied
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <HashRouter>
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-green-50">
          <Navigation
            user={user}
            onLogout={handleLogout}
            onLogin={() => setShowLogin(true)}
            onSignUp={() => setShowSignUp(true)}
          />
          <main className="pt-20">
            <Routes>
              {/* Public: Welcome page (when not logged in) or redirect to /home */}
              <Route
                path="/"
                element={
                  user
                    ? <Navigate to="/home" replace />
                    : <WelcomePage onLogin={() => setShowLogin(true)} onSignUp={() => setShowSignUp(true)} />
                }
              />

              {/* Protected routes */}
              <Route
                path="/home"
                element={
                  <ProtectedRoute user={user}>
                    <Homepage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farmers"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? <AdminFarmersDirectory /> : <UserFarmersDirectory />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/processors"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? <AdminProcessorsDirectory /> : <UserProcessorsDirectory />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? <AdminMap /> : <Maps />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? <AdminServices /> : <Services />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute user={user}>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <ProtectedRoute user={user}>
                    <About />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? (
                      <AdminProfile
                        user={user}
                        onUpdateUser={handleUpdateUser}
                      />
                    ) : (
                      <UserProfile
                        user={user}
                        onUpdateUser={handleUpdateUser}
                      />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute user={user}>
                    <Settings user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/applications"
                element={
                  <ProtectedRoute user={user} adminOnly>
                    <PendingApplications user={user} />
                  </ProtectedRoute>
                }
              />

              {/* 404 catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          {/* Modal overlays */}
          {showLogin && (
            <LoginForm
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              onSwitchToSignUp={switchToSignUp}
              onSwitchToAdminApplication={switchToAdminApplication}
            />
          )}

          {showSignUp && (
            <SignUpForm
              onClose={() => setShowSignUp(false)}
              onSignUp={handleSignUp}
              onSwitchToLogin={switchToLogin}
            />
          )}

          {showAdminApplication && (
            <AdminApplicationForm
              onClose={() => setShowAdminApplication(false)}
              onSuccess={() => setShowAdminApplication(false)}
            />
          )}
        </div>
      </HashRouter>
    </ErrorBoundary>
  );
}
